import React from 'react';
import { Table, Button } from 'react-bootstrap';

type Props = {
  // Per-country editor — required.
  shortName: string;
  // Unique per-CHILD-instance key (Form.tsx childStateKey: child.id for saved,
  // `new:<sn>[:nonce]` for unsaved). Used for ALL DOM id/htmlFor attributes so
  // same-country duplicate tabs (e.g. "JP" + "JP 2") never share an element id.
  // shortName alone is NOT unique across same-market duplicates: a duplicate
  // `id`/`htmlFor` makes a <label> click activate the FIRST matching element in
  // the document (the sibling tab's, which is also mounted), routing the change
  // to the wrong child. Falls back to shortName if a caller omits it.
  instanceKey?: string;
  // Master enable for this market. 'Y' or 'N'/null.
  preQuestionsYN?: string | null;
  onTogglePreScreen?: (e: any) => void;
  // Question pool shared across child tabs (loaded once at the form level).
  preScreenOptions?: any[];
  // Which questions are picked for THIS market (option shape from preScreenOptions).
  selectedPreScreenQues?: any[];
  onPreScreenQuestionsChange?: (selected: any[]) => void;
  // Question IDs whose qualifying answers have been mapped.
  submittedAnswerDets?: string[];
  onUpdateAnswer?: (info: any, index: number) => void;
};

/**
 * PreScreeningSection — per-country pre-screening editor.
 *
 * Renders inside ChildSurveyTab. Each market has its own master toggle, its own
 * picked question list, and its own qualifying-answer mapping. There is no
 * parent-level pre-screening UI; the parent project carries no prescreener
 * configuration. The component is intentionally dumb — all state lives in
 * Form.tsx's childStates[shortName].
 */
export class PreScreeningSection extends React.Component<Props> {
  render() {
    const {
      shortName, instanceKey,
      preQuestionsYN, onTogglePreScreen, preScreenOptions,
      selectedPreScreenQues, onPreScreenQuestionsChange,
      submittedAnswerDets, onUpdateAnswer,
    } = this.props;

    const isEnabled = preQuestionsYN === 'Y';
    // DOM-id scope: unique per child instance. Same-country duplicates must NOT
    // collide on id/htmlFor (see Props.instanceKey).
    const idKey = instanceKey || shortName;

    // Import Select dynamically to avoid circular dependency
    const Select = require('react-select').default;

    return (
      <div>
        <div className="row mt-3">
          <div className="col">
            <div className="custom-control custom-control-inline">
              <input
                type="checkbox"
                id={`prescreen-toggle-${idKey}`}
                name="preQuestionsYN"
                checked={isEnabled}
                className="custom-control-input"
                onChange={onTogglePreScreen}
              />
              <label className="custom-control-label" htmlFor={`prescreen-toggle-${idKey}`}>
                <strong>Add pre screening questions</strong>
              </label>
            </div>
          </div>
        </div>

        {isEnabled && (
          <div className="row mt-2" id={`idPrescreenDiv-${idKey}`}>
            <div className="col" style={{ zIndex: 1200 }}>
              <label htmlFor={`preques-${idKey}`}>Pre Screen Questions</label>
              <Select
                name={`preques-${idKey}`}
                id={`preques-${idKey}`}
                onChange={onPreScreenQuestionsChange}
                value={selectedPreScreenQues}
                isMulti
                options={preScreenOptions}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                formatOptionLabel={(option: any) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{option.label}</span>
                    <span style={{
                      fontSize: '0.7em',
                      padding: '2px 6px',
                      borderRadius: 4,
                      marginLeft: 8,
                      backgroundColor: option.type === 'auto' ? '#e3f2fd' : '#fff3e0',
                      color: option.type === 'auto' ? '#1565c0' : '#e65100',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {option.type === 'auto' ? 'Profile' : 'Custom'}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {isEnabled && selectedPreScreenQues && selectedPreScreenQues.length > 0 && (() => {
          // Split selected questions into Profile (type='auto', shared library)
          // and Custom (type='new', per-PM scoped on the backend) sections.
          const profileQues = selectedPreScreenQues.filter((q: any) => q.type === 'auto');
          const customQues = selectedPreScreenQues.filter((q: any) => q.type === 'new');

          const renderRow = (info: any, index: number) => (
            <tr key={info.value}>
              <td>{index + 1}</td>
              <td>{info.label ?? '-'}</td>
              <td>
                {(() => {
                  if (!submittedAnswerDets?.includes(info.value)) return '-';
                  const isRange = info.questionType === 'Range';
                  const arr = info.selectedAnsArr;
                  if (!arr || !Array.isArray(arr)) return '-';
                  return arr.map((e: any, i: number) => {
                    const answer = isRange ? `${e.answer.fromValue} - ${e.answer.toValue}` : e.answer;
                    let pointsInfo: any = '';
                    if (e.questionQuotaPoints > 0) {
                      if (e.completeCount != null) {
                        pointsInfo = (
                          <>
                            {' '}(<span className="text-danger">{e.questionQuotaPoints - e.completeCount}</span>,{' '}
                            <span className="text-success">{e.completeCount}</span>)
                          </>
                        );
                      } else {
                        pointsInfo = `(${e.questionQuotaPoints})`;
                      }
                    }
                    return (
                      <span key={'ans-' + i}>
                        {answer}{pointsInfo}{i < arr.length - 1 ? ', ' : ''}
                      </span>
                    );
                  });
                })()}
              </td>
              <td>
                <Button
                  variant={submittedAnswerDets?.includes(info.value) ? 'success' : 'warning'}
                  size="sm"
                  className="mx-1"
                  onClick={() => onUpdateAnswer && onUpdateAnswer(info, index)}
                >
                  {submittedAnswerDets?.includes(info.value) ? 'Update Answer' : 'Select Answer'}
                </Button>
              </td>
            </tr>
          );

          const renderSection = (title: string, subtitle: string, items: any[]) => (
            <div className="row mt-3">
              <div className="col">
                <div className="d-flex justify-content-between">
                  <h4>{title}</h4>
                </div>
                <p className="text-muted small mb-2">{subtitle}</p>
                <Table responsive size="sm" bordered>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Question</th>
                      <th>Selected Answers</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((info: any, index: number) => renderRow(info, index))}
                  </tbody>
                </Table>
              </div>
            </div>
          );

          return (
            <>
              {profileQues.length > 0 && renderSection(
                'Profile Questions',
                'Shared question library, available to all PMs.',
                profileQues,
              )}
              {customQues.length > 0 && renderSection(
                'Custom Questions',
                'Per-PM custom questions. Only the creator and admins can edit these.',
                customQues,
              )}
            </>
          );
        })()}
      </div>
    );
  }
}
